// frontend/src/components/maps/RoutePointSearch.js
import React, {
    useRef,
    useState,
  } from "react";
  
  import {
    LuLoaderCircle,
    LuMapPin,
    LuSearch,
    LuTrash2,
  } from "react-icons/lu";
  
  import { useTranslation } from "react-i18next";
  
  import api from "../../axiosConfig";
  
  function formatCoordinates(point) {
    const lat = Number(point?.lat);
    const lng = Number(point?.lng);
  
    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lng)
    ) {
      return null;
    }
  
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }
  
  function getPrimaryText(
    point,
    fallback = ""
  ) {
    return (
      point?.name ||
      point?.city ||
      point?.address ||
      formatCoordinates(point) ||
      fallback
    );
  }
  
  function getSecondaryText(point) {
    const primary =
      getPrimaryText(point);
  
    if (
      point?.address &&
      point.address !== primary
    ) {
      return point.address;
    }
  
    const locationParts = [
      point?.postalCode,
      point?.city,
      point?.country,
    ].filter(Boolean);
  
    if (locationParts.length) {
      return locationParts.join(", ");
    }
  
    return formatCoordinates(point);
  }
  
  export default function RoutePointSearch({
    pointType,
    title,
    point = null,
    icon,
    isMapPicking = false,
    onSelect,
    onToggleMapPicking,
    onRemove,
  }) {
    const { t, i18n } =
      useTranslation("map");
  
    const latestRequestRef =
      useRef(0);
  
    const [query, setQuery] =
      useState("");
  
    const [results, setResults] =
      useState([]);
  
    const [isSearching, setIsSearching] =
      useState(false);
  
    const [searchError, setSearchError] =
      useState(null);
  
    const handleSearch = async (event) => {
      event.preventDefault();
  
      const cleanQuery =
        query.trim();
  
      if (cleanQuery.length < 3) {
        setSearchError(
          t(
            "map.routes.builder.search.minimum",
            {
              defaultValue:
                "Introduza pelo menos 3 caracteres.",
            }
          )
        );
  
        setResults([]);
        return;
      }
  
      const requestId =
        latestRequestRef.current + 1;
  
      latestRequestRef.current =
        requestId;
  
      setIsSearching(true);
      setSearchError(null);
      setResults([]);
  
      try {
        const { data } =
          await api.get(
            "/maps/geocode",
            {
              params: {
                q: cleanQuery,
                language:
                  i18n.resolvedLanguage ||
                  i18n.language ||
                  "pt-PT",
                limit: 6,
              },
            }
          );
  
        if (
          latestRequestRef.current !==
          requestId
        ) {
          return;
        }
  
        const items =
          Array.isArray(data?.items)
            ? data.items
            : [];
  
        setResults(items);
  
        if (!items.length) {
          setSearchError(
            t(
              "map.routes.builder.search.noResults",
              {
                defaultValue:
                  "Nenhum local encontrado.",
              }
            )
          );
        }
      } catch (error) {
        if (
          latestRequestRef.current !==
          requestId
        ) {
          return;
        }
  
        console.error(
          "[RoutePointSearch] Search failed:",
          error
        );
  
        setSearchError(
          t(
            "map.routes.builder.search.error",
            {
              defaultValue:
                "Não foi possível pesquisar o local.",
            }
          )
        );
      } finally {
        if (
          latestRequestRef.current ===
          requestId
        ) {
          setIsSearching(false);
        }
      }
    };
  
    const handleSelectResult = (
      result
    ) => {
      onSelect?.({
        ...result,
        type: pointType,
      });
  
      setQuery("");
      setResults([]);
      setSearchError(null);
    };
  
    return (
      <div
        className={[
          "route-point-search",
          point
            ? "has-selected-point"
            : "",
          isMapPicking
            ? "is-map-picking"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="route-point-search__heading">
          <span className="route-point-search__heading-icon">
            {icon}
          </span>
  
          <div>
            <strong>{title}</strong>
  
            <small>
              {point
                ? t(
                    "map.routes.builder.search.selected",
                    {
                      defaultValue:
                        "Local selecionado",
                    }
                  )
                : t(
                    "map.routes.builder.search.chooseMethod",
                    {
                      defaultValue:
                        "Pesquise ou selecione no mapa.",
                    }
                  )}
            </small>
          </div>
        </div>
  
        {point && (
          <div className="route-point-search__selected">
            <div>
              <strong>
                {getPrimaryText(
                  point,
                  title
                )}
              </strong>
  
              <span>
                {getSecondaryText(point)}
              </span>
            </div>
  
            {onRemove && (
              <button
                type="button"
                onClick={onRemove}
                aria-label={t(
                  "map.routes.builder.search.remove",
                  {
                    defaultValue:
                      "Remover local",
                  }
                )}
              >
                <LuTrash2 />
              </button>
            )}
          </div>
        )}
  
        <form
          className="route-point-search__form"
          onSubmit={handleSearch}
        >
          <LuSearch aria-hidden="true" />
  
          <input
            type="search"
            value={query}
            autoComplete="off"
            onChange={(event) => {
              setQuery(
                event.target.value
              );
  
              setResults([]);
              setSearchError(null);
            }}
            placeholder={t(
              "map.routes.builder.search.placeholder",
              {
                defaultValue:
                  "Pesquisar cidade, morada ou local...",
              }
            )}
            aria-label={t(
              "map.routes.builder.search.placeholder",
              {
                defaultValue:
                  "Pesquisar cidade, morada ou local...",
              }
            )}
          />
  
          <button
            type="submit"
            disabled={isSearching}
          >
            {isSearching ? (
              <LuLoaderCircle className="is-spinning" />
            ) : (
              <span>
                {t(
                  "map.routes.builder.search.action",
                  {
                    defaultValue:
                      "Pesquisar",
                  }
                )}
              </span>
            )}
          </button>
        </form>
  
        {searchError && (
          <div className="route-point-search__error">
            {searchError}
          </div>
        )}
  
        {results.length > 0 && (
          <div className="route-point-search__results">
            <span className="route-point-search__results-label">
              {t(
                "map.routes.builder.search.results",
                {
                  defaultValue:
                    "Resultados",
                }
              )}
            </span>
  
            {results.map(
              (result, index) => (
                <button
                  key={
                    result.id ||
                    `${result.lat}-${result.lng}-${index}`
                  }
                  type="button"
                  className="route-point-search__result"
                  onClick={() =>
                    handleSelectResult(
                      result
                    )
                  }
                >
                  <LuMapPin />
  
                  <span>
                    <strong>
                      {getPrimaryText(
                        result,
                        title
                      )}
                    </strong>
  
                    <small>
                      {getSecondaryText(
                        result
                      )}
                    </small>
                  </span>
                </button>
              )
            )}
  
            <a
              className="route-point-search__attribution"
              href="https://www.openstreetmap.org/copyright"
              target="_blank"
              rel="noreferrer"
            >
              © OpenStreetMap contributors
            </a>
          </div>
        )}
  
        <button
          type="button"
          className={[
            "route-point-search__map-button",
            isMapPicking
              ? "is-active"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={
            onToggleMapPicking
          }
        >
          <LuMapPin />
  
          <span>
            {isMapPicking
              ? t(
                  "map.routes.builder.search.selectingOnMap",
                  {
                    defaultValue:
                      "Clique agora no mapa...",
                  }
                )
              : t(
                  "map.routes.builder.search.selectOnMap",
                  {
                    defaultValue:
                      "Selecionar no mapa",
                  }
                )}
          </span>
        </button>
      </div>
    );
  }